import React, { useEffect, useState } from 'react';
import apiService from '../../../services/apiService';

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<any[]>([]);
  const [roles, setRoles] = useState<any[]>([]);
  const [branches, setBranches] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Get logged-in user context
  const crmUserStr = localStorage.getItem('aswCrmUser');
  const loggedInUser = crmUserStr ? JSON.parse(crmUserStr) : null;
  const isAdminUser = loggedInUser?.role === 'Admin' || loggedInUser?.role === 'Branch Manager';

  const [showAddForm, setShowAddForm] = useState(false);
  const [newUser, setNewUser] = useState({ 
    name: '', 
    email: '', 
    password: '', 
    role: '', 
    mobile: '', 
    department: 'Sales', 
    designation: '',
    max_workload: 10,
    performance_score: 100,
    is_senior: false,
    availability_status: 'Offline',
    branch_id: loggedInUser?.branch_id || ''
  });
  const [editingUser, setEditingUser] = useState<any>(null);
  const [newRoleName, setNewRoleName] = useState('');
  const [newRoleStep, setNewRoleStep] = useState('');
  const [showNewRole, setShowNewRole] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingRole, setSavingRole] = useState(false);

  const WORKFLOW_STEPS = [
    'Booking',
    'Document Complete',
    'Loan Login',
    'Verification',
    'Account Opening',
    'Agreement',
    'Down Payment',
    'Disbursement',
    'RTO (Passing)',
    'Delivery'
  ];

  const DEPARTMENTS = ['Sales', 'Loan', 'RTO', 'Delivery', 'Admin'];

  const fetchData = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const [usersRes, rolesRes, branchesRes] = await Promise.all([
        apiService.getCrmUsers(),
        apiService.getCrmRoles(),
        apiService.getCrmBranches()
      ]);
      
      if (usersRes.success) setUsers(usersRes.data);
      if (rolesRes.success) {
        setRoles(rolesRes.data);
        if (!newUser.role && rolesRes.data.length > 0) {
          setNewUser(u => ({ ...u, role: rolesRes.data[0].name }));
        }
      }
      if (branchesRes && branchesRes.success) {
        setBranches(branchesRes.data);
      }
    } catch (err) { 
      console.error(err); 
    } finally { 
      if (!silent) setLoading(false); 
    }
  };

  useEffect(() => { 
    fetchData(); 
    const timer = setInterval(() => {
      fetchData(true);
    }, 20000);
    return () => clearInterval(timer);
  }, []);

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await apiService.createCrmUser(newUser);
      if (res.success) {
        setShowAddForm(false);
        setNewUser({ 
          name: '', 
          email: '', 
          password: '', 
          role: roles[0]?.name || '', 
          mobile: '', 
          department: 'Sales', 
          designation: '',
          max_workload: 10,
          performance_score: 100,
          is_senior: false,
          availability_status: 'Available',
          branch_id: loggedInUser?.branch_id || ''
        });
        fetchData();
      }
    } catch { 
      alert('Error creating user. Email may already exist.'); 
    } finally { 
      setSaving(false); 
    }
  };

  const handleUpdateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Don't send empty passwords
      const payload = { ...editingUser };
      if (!payload.password) delete payload.password;
      
      const res = await apiService.updateCrmUser(editingUser.id, payload);
      if (res.success) {
        setEditingUser(null);
        fetchData();
      }
    } catch { 
      alert('Error updating user.'); 
    } finally { 
      setSaving(false); 
    }
  };

  const handleDeleteUser = async (id: string, name: string) => {
    if (!window.confirm(`Are you absolutely sure you want to delete ${name}?\n\nWarning: This is permanent and may break historical logs.`)) return;
    
    try {
      const res = await apiService.deleteCrmUser(id);
      if (res.success) fetchData();
    } catch { 
      alert('Error deleting user.'); 
    }
  };

  const handleCreateRole = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newRoleName.trim()) return;
    setSavingRole(true);
    try {
      const res = await apiService.createCrmRole({ name: newRoleName, workflow_step: newRoleStep || null });
      if (res.success) {
        setNewRoleName('');
        setNewRoleStep('');
        setShowNewRole(false);
        fetchData();
      }
    } catch { 
      alert('Error creating role — it may already exist.'); 
    } finally { 
      setSavingRole(false); 
    }
  };

  const getStatusStyle = (status: string) => {
    const s = (status || 'Active').toLowerCase();
    if (s === 'active') return { background: '#D1FAE5', color: '#065F46' };
    if (s === 'inactive') return { background: '#FEF3C7', color: '#92400E' };
    return { background: '#FEE2E2', color: '#991B1B' }; // Deactivated
  };

  if (loading) return <div className="crm-loading"><div className="crm-spinner"></div> Loading...</div>;

  return (
    <div>
      <div className="crm-page-header">
        <div className="crm-page-header-left">
          <h2>Employee & Department Management</h2>
          <p>{users.length} registered employees · {roles.length} roles defined</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="crm-btn crm-btn-ghost" onClick={() => setShowNewRole(v => !v)}>
            {showNewRole ? '✕' : '⚙ New Role'}
          </button>
          <button className="crm-btn crm-btn-primary" onClick={() => setShowAddForm(v => !v)}>
            {showAddForm ? '✕ Cancel' : '+ Add Employee'}
          </button>
        </div>
      </div>

      {/* Create New Role Form */}
      {showNewRole && (
        <form className="crm-form-panel" onSubmit={handleCreateRole}>
          <h3>⚙ Create New Role</h3>
          <div className="crm-form-fields">
            <div className="crm-form-field">
              <label>Role Name *</label>
              <input type="text" placeholder="e.g. Insurance Team" required value={newRoleName} onChange={e => setNewRoleName(e.target.value)} />
            </div>
            <div className="crm-form-field">
              <label>Responsible Workflow Step (optional)</label>
              <select value={newRoleStep} onChange={e => setNewRoleStep(e.target.value)}>
                <option value="">None / Admin only</option>
                {WORKFLOW_STEPS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div className="crm-form-actions">
            <button type="submit" className="crm-btn crm-btn-success" disabled={savingRole}>
              {savingRole ? 'Creating…' : '✓ Create Role'}
            </button>
          </div>
        </form>
      )}

      {/* Add Employee Form */}
      {showAddForm && (
        <form className="crm-form-panel" onSubmit={handleAddUser}>
          <h3>➕ Register New Employee</h3>
          <div className="crm-form-fields">
            <div className="crm-form-field">
              <label>Full Name *</label>
              <input type="text" placeholder="e.g. Rahul Sharma" required value={newUser.name} onChange={e => setNewUser({ ...newUser, name: e.target.value })} />
            </div>
            <div className="crm-form-field">
              <label>Email Address *</label>
              <input type="email" placeholder="rahul@asw.com" required value={newUser.email} onChange={e => setNewUser({ ...newUser, email: e.target.value })} />
            </div>
            <div className="crm-form-field">
              <label>Create Password *</label>
              <input type="text" placeholder="Set initial password" required value={newUser.password} onChange={e => setNewUser({ ...newUser, password: e.target.value })} />
            </div>
            <div className="crm-form-field">
              <label>Mobile Number</label>
              <input type="text" placeholder="+91 98765..." value={newUser.mobile} onChange={e => setNewUser({ ...newUser, mobile: e.target.value })} />
            </div>
            <div className="crm-form-field">
              <label>Department *</label>
              <select value={newUser.department} onChange={e => setNewUser({ ...newUser, department: e.target.value })}>
                {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div className="crm-form-field">
              <label>Designation</label>
              <input type="text" placeholder="e.g. Senior executive" value={newUser.designation} onChange={e => setNewUser({ ...newUser, designation: e.target.value })} />
            </div>
            <div className="crm-form-field">
              <label>System Role *</label>
              <select value={newUser.role} onChange={e => setNewUser({ ...newUser, role: e.target.value })}>
                {roles.map(r => <option key={r.id} value={r.name}>{r.name}{r.workflow_step ? ` → ${r.workflow_step}` : ''}</option>)}
              </select>
            </div>
            {isAdminUser && (
              <div className="crm-form-field">
                <label>Branch Office *</label>
                <select value={newUser.branch_id} onChange={e => setNewUser({ ...newUser, branch_id: e.target.value })}>
                  <option value="">Main Branch/Thane</option>
                  {branches.map((b: any) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
            )}
            <div className="crm-form-field">
              <label>Max Workload Limit</label>
              <input type="number" min="1" max="10000" value={newUser.max_workload} onChange={e => setNewUser({ ...newUser, max_workload: parseInt(e.target.value) || 10 })} />
            </div>
            <div className="crm-form-field">
              <label>Performance Score (0 - 100)</label>
              <input type="number" min="0" max="100" value={newUser.performance_score} onChange={e => setNewUser({ ...newUser, performance_score: parseInt(e.target.value) || 100 })} />
            </div>
            <div className="crm-form-field" style={{ display: 'flex', alignItems: 'center', marginTop: '24px', gap: '8px' }}>
              <input type="checkbox" id="is_senior_new" checked={newUser.is_senior} onChange={e => setNewUser({ ...newUser, is_senior: e.target.checked })} style={{ width: 'auto', margin: 0 }} />
              <label htmlFor="is_senior_new" style={{ cursor: 'pointer', margin: 0, fontWeight: 'normal' }}>Senior Representative</label>
            </div>
          </div>
          <div className="crm-form-actions">
            <button type="submit" className="crm-btn crm-btn-success" disabled={saving}>{saving ? 'Registering…' : '✓ Register Employee'}</button>
          </div>
        </form>
      )}

      {/* Edit Employee Form */}
      {editingUser && (
        <form className="crm-form-panel" style={{ border: '2px solid var(--crm-primary-light)' }} onSubmit={handleUpdateUser}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ margin: 0 }}>✏️ Edit Employee: {editingUser.name} ({editingUser.employee_id || 'No ID'})</h3>
            <button type="button" onClick={() => setEditingUser(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18 }}>✕</button>
          </div>
          <div className="crm-form-fields">
            <div className="crm-form-field">
              <label>Full Name *</label>
              <input type="text" required value={editingUser.name || ''} onChange={e => setEditingUser({ ...editingUser, name: e.target.value })} />
            </div>
            <div className="crm-form-field">
              <label>Mobile Number</label>
              <input type="text" value={editingUser.mobile || ''} onChange={e => setEditingUser({ ...editingUser, mobile: e.target.value })} />
            </div>
            <div className="crm-form-field">
              <label>Department *</label>
              <select value={editingUser.department || 'Sales'} onChange={e => setEditingUser({ ...editingUser, department: e.target.value })}>
                {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div className="crm-form-field">
              <label>Designation</label>
              <input type="text" value={editingUser.designation || ''} onChange={e => setEditingUser({ ...editingUser, designation: e.target.value })} />
            </div>
            <div className="crm-form-field">
              <label>System Role *</label>
              <select value={editingUser.role} onChange={e => setEditingUser({ ...editingUser, role: e.target.value })}>
                {roles.map(r => <option key={r.id} value={r.name}>{r.name}{r.workflow_step ? ` → ${r.workflow_step}` : ''}</option>)}
              </select>
            </div>
            {isAdminUser && (
              <div className="crm-form-field">
                <label>Branch Office *</label>
                <select value={editingUser.branch_id || ''} onChange={e => setEditingUser({ ...editingUser, branch_id: e.target.value })}>
                  <option value="">Main Branch/Thane</option>
                  {branches.map((b: any) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
            )}
            <div className="crm-form-field">
              <label>Account Status *</label>
              <select value={editingUser.status || 'Active'} onChange={e => setEditingUser({ ...editingUser, status: e.target.value })}>
                <option value="Active">🟢 Active</option>
                <option value="Inactive">🟡 Inactive</option>
                <option value="Deactivated">🔴 Deactivated</option>
              </select>
            </div>
            <div className="crm-form-field">
              <label>Reset Password (Optional)</label>
              <input type="text" placeholder="Type new password or leave blank" value={editingUser.password || ''} onChange={e => setEditingUser({ ...editingUser, password: e.target.value })} />
            </div>
            <div className="crm-form-field">
              <label>Availability Status</label>
              <select value={editingUser.availability_status || 'Available'} onChange={e => setEditingUser({ ...editingUser, availability_status: e.target.value })}>
                <option value="Available">🟢 Available</option>
                <option value="Busy">🔴 Busy</option>
                <option value="Break">🟡 Break</option>
                <option value="Offline">⚫ Offline</option>
              </select>
            </div>
            <div className="crm-form-field">
              <label>Max Workload Limit</label>
              <input type="number" min="1" max="10000" value={editingUser.max_workload || 10} onChange={e => setEditingUser({ ...editingUser, max_workload: parseInt(e.target.value) || 10 })} />
            </div>
            <div className="crm-form-field">
              <label>Performance Score (0 - 100)</label>
              <input type="number" min="0" max="100" value={editingUser.performance_score || 100} onChange={e => setEditingUser({ ...editingUser, performance_score: parseInt(e.target.value) || 100 })} />
            </div>
            <div className="crm-form-field" style={{ display: 'flex', alignItems: 'center', marginTop: '24px', gap: '8px' }}>
              <input type="checkbox" id="is_senior_edit" checked={editingUser.is_senior || false} onChange={e => setEditingUser({ ...editingUser, is_senior: e.target.checked })} style={{ width: 'auto', margin: 0 }} />
              <label htmlFor="is_senior_edit" style={{ cursor: 'pointer', margin: 0, fontWeight: 'normal' }}>Senior Representative</label>
            </div>
          </div>
          <div className="crm-form-actions" style={{ marginTop: 20 }}>
            <button type="button" className="crm-btn crm-btn-ghost" onClick={() => setEditingUser(null)}>Cancel</button>
            <button type="submit" className="crm-btn crm-btn-success" disabled={saving}>{saving ? 'Saving…' : '✓ Save Changes'}</button>
          </div>
        </form>
      )}

      {/* Roles Overview */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--crm-text-secondary)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Active Roles ({roles.length})</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {roles.map(r => (
            <span key={r.id} style={{ padding: '5px 12px', background: 'white', border: '1.5px solid var(--crm-border)', borderRadius: '20px', fontSize: '12.5px', fontWeight: 600, color: 'var(--crm-text-primary)' }}>
              {r.name}{r.workflow_step ? <span style={{ color: 'var(--crm-primary)', marginLeft: 4 }}>→ {r.workflow_step}</span> : ''}
            </span>
          ))}
        </div>
      </div>

      {/* Employees Table */}
      <div className="crm-table-wrapper">
        <table className="crm-table">
          <thead>
            <tr>
              <th>Employee ID</th>
              <th>Full Name</th>
              <th>Email</th>
              <th>Department / Designation</th>
              <th>Role</th>
              <th>Branch</th>
              <th>Status</th>
              <th>Availability</th>
              <th>Workload</th>
              <th>Added On</th>
              <th style={{ textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user: any) => {
              const statusStyle = getStatusStyle(user.status);
              return (
                <tr key={user.id}>
                  <td style={{ fontWeight: 700, color: 'var(--crm-text-primary)' }}>
                    {user.employee_id || '—'}
                  </td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{
                        width: 34, height: 34, borderRadius: '50%',
                        background: 'linear-gradient(135deg, var(--crm-primary-light), var(--crm-info-light))',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontWeight: 700, fontSize: 13, color: 'var(--crm-primary)', flexShrink: 0
                      }}>
                        {user.name?.charAt(0)?.toUpperCase()}
                      </div>
                      <div>
                        <div style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                          {user.name}
                          {user.is_senior && <span style={{ color: '#F59E0B' }} title="Senior Representative">⭐</span>}
                        </div>
                        {user.mobile && <div style={{ fontSize: '11px', color: 'var(--crm-text-muted)' }}>📱 {user.mobile}</div>}
                      </div>
                    </div>
                  </td>
                  <td style={{ color: 'var(--crm-text-secondary)', fontSize: '13px' }}>{user.email}</td>
                  <td>
                    <div style={{ fontWeight: 600, fontSize: '13px' }}>{user.department || '—'}</div>
                    {user.designation && <div style={{ fontSize: '11.5px', color: 'var(--crm-text-muted)' }}>{user.designation}</div>}
                  </td>
                  <td>
                    <span className="crm-status-badge" style={{ background: 'var(--crm-primary-light)', color: 'var(--crm-primary)' }}>
                      {user.role}
                    </span>
                  </td>
                  <td>
                    {(() => {
                      const branch = branches.find((b: any) => b.id === user.branch_id);
                      return branch ? (
                        <span className="crm-status-badge" style={{ background: '#F1F5F9', color: '#334155', fontWeight: 600 }}>
                          🏢 {branch.name}
                        </span>
                      ) : (
                        <span style={{ color: 'var(--crm-text-muted)', fontSize: '12px' }}>Main Branch/Thane</span>
                      );
                    })()}
                  </td>
                  <td>
                    <span className="crm-status-badge" style={{ background: statusStyle.background, color: statusStyle.color }}>
                      {user.status || 'Active'}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {(() => {
                        const status = user.availability_status || 'Offline';
                        const colors: Record<string, string> = { Available: '#10B981', Busy: '#EF4444', Break: '#F59E0B', Offline: '#6B7280' };
                        const color = colors[status] || '#6B7280';
                        return (
                          <span style={{ fontSize: '12.5px', fontWeight: 600, color, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, display: 'inline-block' }}></span>
                            {status}
                          </span>
                        );
                      })()}
                      {(() => {
                        if (!user.last_active_at) return <span style={{ fontSize: '11px', color: 'var(--crm-text-muted)' }}>Never active</span>;
                        const mins = Math.max(0, Math.floor((Date.now() - new Date(user.last_active_at).getTime()) / 60000));
                        return <span style={{ fontSize: '11px', color: 'var(--crm-text-muted)' }}>Seen: {mins < 5 ? 'now' : mins < 60 ? `${mins}m ago` : `${Math.floor(mins / 60)}h ago`}</span>;
                      })()}
                    </div>
                  </td>
                  <td>
                    <div style={{ fontWeight: 600, fontSize: '13px' }}>
                      {user.workload?.leads || 0} / {user.max_workload || 10}
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--crm-text-muted)' }}>open leads</div>
                  </td>
                  <td style={{ color: 'var(--crm-text-muted)', fontSize: '12.5px' }}>
                    {new Date(user.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' })}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <button 
                      className="crm-btn crm-btn-ghost" 
                      style={{ padding: '4px 8px', fontSize: '12px', marginRight: '6px' }}
                      onClick={() => {
                        setEditingUser({ ...user, password: '' }); 
                        setShowAddForm(false);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                    >
                      ✏️ Edit
                    </button>
                    <button 
                      className="crm-btn crm-btn-ghost" 
                      style={{ padding: '4px 8px', fontSize: '12px', color: 'var(--crm-danger)' }}
                      onClick={() => handleDeleteUser(user.id, user.name)}
                    >
                      🗑️ Delete
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UserManagement;
